import { Box, Grid, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, isValid } from "date-fns";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch } from "../../../../app/hooks";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import { ReactComponent as ArrowLeft } from "../../../../common/icons/arrowLeft.svg";
import { SupplementPriceOverridesType } from "../../../../common/types/apiTypes";
import useIsLoading from "../../../../hooks/useIsLoading";
import useMessage from "../../../../hooks/useMessage";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import { fetchWorkLocationWithOrgById } from "../../../customer/customerWorkLocationsSlice";
import { fetchOrderQueueById } from "../../../settings/settingsActions";
import { canExpertPlaceAndEditOrders } from "../../../user/accessRightChecks";
import OrdersHeader from "../../OrdersHeader";
import { EditOrderActionBanner } from "../../actionBanners/EditOrderActionBanner";
import {
  AUTOMATCHING_ON,
  AUTOMATCHING_PAUSED,
  CancelAssignmentScope,
  OrderDialogMode,
} from "../../constants";
import OrderShortDataProvider from "../../newOrder/short/OrderShortDataProvider";
import { OffersStatus, OrderEdit, OrderHandler } from "../../orderAPI";
import { isAutomatchingDelayTooLong } from "../../automatchingDelayValidation";
import { parseToInterval } from "../../OrderTimeField";
import {
  cancelRowBundle,
  cancelShortOrderShift,
  disassembleOrderRowBundle,
  fetchOrderFulfilledStatus,
  saveOrderRow,
  shiftCancelled,
} from "../../orderRowsDetailedSlice";
import { AutomatchingStatus, ValidationError } from "../../orderTypes";
import {
  CancelAllAssignmentsCallback,
  CancelAssignmentCallback,
  HandleAdditionalInfoChangeFunction,
  HandleAmountChangeFunction,
  HandleAutomatchingStatusChangeFunction,
  HandleCustomerCCChangeFunction,
  HandleDescriptionsChangeFunction,
  HandleFillShiftChangeFunction,
  HandleFlexibleStartTimeChangeFunction,
  HandlePeriodStartDateChangeFunction,
  HandleRequirementsChangeFunction,
  HandleSalaryLimitChangeFunction,
  HandleSkillsChangeFunction,
  HandleSupplementPriceOverrideChangeFunction,
  HandleSupplementsChangeFunction,
  HandleTesVariationChangeFunction,
  HandleTimeChangeFunction,
  ShiftSummary,
} from "../../types/editOrder";
import {
  automatchingIsNotInThePast,
  getCustomerCostCenterName,
} from "../../utils";
import { fetchOrderRowsOfferCount } from "../../workOffersSlice";
import { CancelReasonBanner } from "../CancelReasonBanner";
import { EditOrderDialogs } from "../EditOrderDialogs";
import { FulfillmentStatusNotification } from "../FulfillmentStatusNotification";
import { OrderHandlerSelection } from "../OrderHandlerSelection";
import { EditShortOrderInfo } from "./EditShortOrderInfo";
import { ShortOrderWorkOffers } from "./ShortOrderWorkOffers";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../common/helpers/formatDateTime";

type Props = {
  order: OrderEdit;
};

export const EditShortOrder = ({ order: initialOrderProp }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sendMessage } = useMessage();
  const { bundleId } = useParams();
  const { t } = useTranslation("order");
  const { user, fullName: currentUserFullName } = useCurrentUser();

  const [tabIndex, setTabIndex] = useState(0);
  const [openMode, setOpenMode] = useState<OrderDialogMode>(
    OrderDialogMode.None,
  );
  const [cancelAssignmentScope, setCancelAssignmentScope] =
    useState<CancelAssignmentScope>(CancelAssignmentScope.Single);
  const [cancelledShifts, setCancelledShifts] = useState<ShiftSummary[]>([]);

  const initialOrder = useMemo(() => initialOrderProp, [initialOrderProp]);
  const [order, setOrder] = useState<OrderEdit>(initialOrderProp);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );

  const [offers, setOffers] = useState<OffersStatus>({
    automatchOffers: {
      workOffersTotal: 0,
      workOffersCancelled: 0,
      workOffersAccepted: 0,
      workOffersDeclined: 0,
      workOffersOffered: 0,
      workOffersNoResponse: 0,
    },
    manualOffers: {
      workOffersTotal: 0,
      workOffersCancelled: 0,
      workOffersAccepted: 0,
      workOffersDeclined: 0,
      workOffersOffered: 0,
      workOffersNoResponse: 0,
    },
  });

  // TODO: TYPE?
  const [options, setOptions] = useState<{
    tesOptions: { id: string; name: string }[];
    workLocations: any[];
    costCenters: any[];
  }>({
    tesOptions: [{ id: order?.orderRows[0].taskSpec.tesId, name: "" }],
    workLocations: [],
    costCenters: [],
  });
  const [orderHandlerOptions, setOrderHandlerOptions] = useState<
    OrderHandler[]
  >([]);

  const [removeShiftsFromBundleIds, setRemoveShiftsFromBundleIds] = useState<
    { id: string; version: number }[]
  >([]);
  const [cancelAssignmentIdAndRowId, setCancelAssignmentIdAndRowId] = useState<{
    assignmentId: string;
    rowId: string;
  }>({ assignmentId: "", rowId: "" });
  const [cancelAssignmentPaySalary, setCancelAssignmentPaySalary] =
    useState<boolean>(false);
  const [cancelAssignmentTalentId, setCancelAssignmentTalentId] = useState<
    string | null
  >(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [periodStartDateIsRequired, setPeriodStartDateIsRequired] = useState<
    boolean | null
  >(null);

  // YLPE-6651: get actual fulfillment status with separate endpoint because of bugs.
  // Use this info for edit permissions and what to show in shift info.
  const [isOrderFulfilled, setIsOrderFulfilled] = useState<boolean>(true);

  const isAutomatchingOn =
    initialOrder.status?.automatchingStatus === AUTOMATCHING_ON;
  const isAutomatchingActiveOrPaused =
    initialOrder.status?.automatchingStatus === AUTOMATCHING_ON ||
    initialOrder.status?.automatchingStatus === AUTOMATCHING_PAUSED;
  const hasExpertEditRights = canExpertPlaceAndEditOrders(user!!);
  const isExpertAuthorizedToEdit = !!order.orderHandler
    ? order.orderHandler.id === user?.id
    : hasExpertEditRights;
  const isReadOnly = isOrderFulfilled || !!initialOrder.cancelReason;
  const isCancelDisabled =
    order.shiftDetails?.some(
      (sd) => !!sd.assignedTalent && !sd.assignedTalent.reasonForCancellation,
    ) || isReadOnly;

  const baseLoading = useIsLoading([
    "orderRowsDetailed",
    "customerWorkLocations",
  ]);
  const isLoading = isCancelling || baseLoading;

  const getOrderFulfilledStatus = useCallback(() => {
    dispatch(fetchOrderFulfilledStatus({ bundleId: bundleId!! })).then(
      (action) =>
        isFulfilled(action) &&
        setIsOrderFulfilled(action.payload?.fulfilled || false),
    );
  }, [bundleId, dispatch]);

  useEffect(() => {
    getOrderFulfilledStatus();
  }, [bundleId, dispatch, getOrderFulfilledStatus]);

  const cancelOrderRows = (reasonForCancellation: string) => {
    const data = {
      bundleId: order.bundleId,
      reasonForCancellation,
    };
    dispatch(cancelRowBundle(data)).then((action) => {
      if (isFulfilled(action)) {
        setOpenMode(OrderDialogMode.None);
        navigate("/home/orders");
      }
    });
  };
  const handleCancelAssignmentInfo: CancelAssignmentCallback = (
    assignmentId,
    rowId,
    paySalary,
  ) => {
    setCancelAssignmentIdAndRowId({ assignmentId, rowId });
    setCancelAssignmentPaySalary(paySalary);
    setCancelAssignmentScope(CancelAssignmentScope.Single);
    setCancelledShifts([]);
    setCancelAssignmentTalentId(null);
    setOpenMode(OrderDialogMode.CancelAssignment);
  };

  const handleCancelAllAssignments: CancelAllAssignmentsCallback = (
    talentId,
    paySalary,
  ) => {
    const shiftsForTalent = order.shiftDetails.filter(
      (shift) =>
        shift.assignedTalent?.id === talentId &&
        !shift.assignedTalent?.reasonForCancellation,
    );
    setCancelledShifts(
      shiftsForTalent.map((shift) => ({
        id: shift.id,
        start: shift.shiftDetail.time.start,
        end: shift.shiftDetail.time.end,
        locationName: getCustomerCostCenterName(
          options.costCenters,
          shift.shiftDetail.custCCId,
        ),
        talentName: shift.assignedTalent?.name,
      })),
    );
    setCancelAssignmentScope(CancelAssignmentScope.Bundle);
    setCancelAssignmentPaySalary(paySalary);
    setCancelAssignmentTalentId(talentId);
    setOpenMode(OrderDialogMode.CancelAssignment);
  };

  const hasAssignmentCancelEvent = (action: unknown) =>
    isFulfilled(action) &&
    Array.isArray(action.payload) &&
    action.payload.some(
      (event: { name?: string }) => event.name === "assgnCancel",
    );

  const cancelAssignment = async (
    reason: string,
    replaceTalent = false,
    setAutomatchingStatus?: AutomatchingStatus,
  ) => {
    // TODO: consider creating a new thunk for this sequence of actions
    setIsCancelling(true);
    setOpenMode(OrderDialogMode.None);

    try {
      if (cancelAssignmentScope === CancelAssignmentScope.Bundle) {
        const shiftsForTalent = order.shiftDetails.filter(
          (shift) =>
            shift.assignedTalent?.id === cancelAssignmentTalentId &&
            !shift.assignedTalent?.reasonForCancellation,
        );
        const cancelResults = await Promise.all(
          shiftsForTalent.map((shift) =>
            dispatch(
              cancelShortOrderShift({
                assignmentId: shift.assignedTalent?.assignmentId!,
                paySalary: cancelAssignmentPaySalary,
                reason,
                bundleId: bundleId!,
                rowId: shift.orderRowId,
              }),
            ),
          ),
        );
        if (!cancelResults.every(hasAssignmentCancelEvent)) {
          return;
        }

        await dispatch(
          shiftCancelled({
            bundleId: bundleId!,
            talentId: cancelAssignmentTalentId!,
            orderRowIds: Array.from(
              new Set(shiftsForTalent.map((shift) => shift.orderRowId)),
            ),
            replaceTalent,
            setAutomatchingStatus,
            editor: currentUserFullName,
          }),
        );

        if (cancelResults.some((action) => isFulfilled(action))) {
          getOrderFulfilledStatus();
        }
        return;
      }

      const data = {
        assignmentId: cancelAssignmentIdAndRowId.assignmentId,
        paySalary: cancelAssignmentPaySalary,
        reason,
        bundleId: bundleId!,
        rowId: cancelAssignmentIdAndRowId.rowId,
      };
      const action = await dispatch(cancelShortOrderShift(data));
      if (!hasAssignmentCancelEvent(action)) {
        return;
      }

      const resolvedTalentId =
        order.shiftDetails.find(
          (shift) =>
            shift.assignedTalent?.assignmentId ===
            cancelAssignmentIdAndRowId.assignmentId,
        )?.assignedTalent?.id ?? null;
      if (!resolvedTalentId) {
        return;
      }
      await dispatch(
        shiftCancelled({
          bundleId: bundleId!,
          talentId: resolvedTalentId,
          orderRowIds: [cancelAssignmentIdAndRowId.rowId],
          replaceTalent,
          setAutomatchingStatus,
          editor: currentUserFullName,
        }),
      );

      if (isFulfilled(action)) {
        getOrderFulfilledStatus();
      }
    } finally {
      setIsCancelling(false);
    }
  };

  useEffect(() => {
    if (!order.workLocationId || !order.customerOrgId) return;
    dispatch(
      fetchWorkLocationWithOrgById({
        workLocationId: order.workLocationId,
        customerOrgId: order.customerOrgId,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        // FIXME: different types for customerCostCenters
        const workLocationsCustCC = action.payload.workLocations[0].customerCCs;
        const customerOrgsCustCC =
          action.payload.customerOrganizations[0].costCenters || [];
        const custCCOptions =
          workLocationsCustCC.length > 0
            ? workLocationsCustCC
            : customerOrgsCustCC;

        setOptions((options) => ({
          ...options,
          costCenters: custCCOptions,
        }));
      }
    });
  }, [dispatch, order.customerOrgId, order.workLocationId]);

  const handleTabChange = (event: SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };
  const tabs = [
    { label: t("order"), value: 0 },
    { label: t("orderTalentState"), value: 1 },
  ];

  // REVIEW: maybe these handlers should be moved to EditShortOrderInfo and only pass setOrder (2025-09-24)
  const handleAmountChange: HandleAmountChangeFunction = (amount) => {
    setOrder({ ...order, amount });
  };

  const handleTimeChange: HandleTimeChangeFunction = (rowId, time) => {
    // TODO fix time string length
    const startString = time.slice(0, 4);
    const endString = time.slice(4, 8);

    if (startString.length !== 4 || endString.length !== 4) return;

    const row = order.orderRows.find((row) => row.id === rowId);
    if (row) {
      const { startDateTime: start, endDateTime: end } = parseToInterval(
        `${startString}${endString}`,
        new Date(row.shiftDetail.time.start),
      );

      // Note: remove updatedRows after orderRows field is deprecated
      const updatedRows = order.orderRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              shiftDetail: { ...row.shiftDetail, time: { start, end } },
            }
          : row,
      );
      const updatedShiftDetails = order.shiftDetails.map((sd) =>
        sd.id === row.id
          ? {
              ...sd,
              shiftDetail: {
                ...sd.shiftDetail,
                time: { start, end },
              },
            }
          : sd,
      );

      setOrder({
        ...order,
        orderRows: updatedRows,
        shiftDetails: updatedShiftDetails,
      });
    }
  };

  const handleCustomerCCChange: HandleCustomerCCChangeFunction = (
    orderRowId,
    shiftDetailId,
    customerCC,
  ) => {
    if (customerCC) {
      const row = order.orderRows.find((row) => row.id === orderRowId);
      if (row) {
        const updatedShiftDetails = order.shiftDetails.map((sd) =>
          sd.id === shiftDetailId
            ? {
                ...sd,
                shiftDetail: { ...sd.shiftDetail, custCCId: customerCC.id },
              }
            : sd,
        );
        const updatedRow = {
          ...row,
          shiftDetail: { ...row.shiftDetail, custCCId: customerCC.id },
        };
        const updatedRows = order.orderRows.map((row) =>
          row.id === orderRowId ? updatedRow : row,
        );
        setOrder({
          ...order,
          orderRows: updatedRows,
          shiftDetails: updatedShiftDetails,
        });
      }
    }
  };

  const handleDescriptionsChange: HandleDescriptionsChangeFunction = (
    descriptions,
  ) => {
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      taskSpec: { ...row.taskSpec, ...descriptions },
    }));
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleAdditionalInfoChange: HandleAdditionalInfoChangeFunction = (
    key: string,
    info: string,
  ) => {
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      [key]: info,
    }));
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleTesVariationChange: HandleTesVariationChangeFunction = (
    variation,
  ) => {
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      taskSpec: {
        ...row.taskSpec,
        tesVariation: variation,
      },
    }));
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleSalaryLimitChange: HandleSalaryLimitChangeFunction = (
    salaryLimit,
  ) => {
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      shiftDetail: { ...row.shiftDetail, salaryLimit },
    }));
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handlePeriodStartDateChange: HandlePeriodStartDateChangeFunction = (
    pStartDate,
  ) => {
    const periodStartDate = isValid(pStartDate)
      ? format(pStartDate!, isoFormat)
      : pStartDate;
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      taskSpec: { ...row.taskSpec, periodStartDate },
    }));
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleRequirementsChange: HandleRequirementsChangeFunction = (
    certificates,
    trainings,
    languages,
  ) => {
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      taskSpec: {
        ...row.taskSpec,
        certificateIds: certificates.map((certificate) => certificate.id),
        trainings: trainings.map((training) => training.id),
        languageSkills: languages,
      },
    }));
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleFillShiftChange: HandleFillShiftChangeFunction = (
    rowId,
    isFillShift,
  ) => {
    const orderRows = order.orderRows.map((row) =>
      row.id === rowId
        ? {
            ...row,
            shiftDetail: {
              ...row.shiftDetail,
              isFillShift,
            },
          }
        : row,
    );
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleSkillsChange: HandleSkillsChangeFunction = (skills) => {
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      taskSpec: {
        ...row.taskSpec,
        skillIds: skills.map((skill) => skill.id),
      },
    }));
    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleAutomatchingStatusChange: HandleAutomatchingStatusChangeFunction =
    (checked) => {
      const automatchingStatus = checked
        ? AutomatchingStatus.On
        : AutomatchingStatus.Off;

      setOrder((order) => ({
        ...order,
        status: {
          ...order.status,
          automatchingStatus,
        },
      }));
    };

  const handleAutomatchingDelayChange = (minutes: number) => {
    setOrder((prev) => ({
      ...prev,
      status: {
        ...prev.status,
        automatchingStartDelayMinutes: Math.max(1, minutes),
      },
    }));
  };

  const handleSupplementsChange: HandleSupplementsChangeFunction = (
    key,
    value,
  ) => {
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      taskSpec: {
        ...row.taskSpec,
        supplements: {
          ...value,
        },
      },
    }));

    setOrder((order) => ({ ...order, orderRows }));
  };

  const handleSupplementPriceOverridesChange: HandleSupplementPriceOverrideChangeFunction =
    (_: string, value: SupplementPriceOverridesType) => {
      const orderRows = order.orderRows.map((row) => ({
        ...row,
        taskSpec: {
          ...row.taskSpec,
          supplementPriceOverrides: {
            ...value,
          },
        },
      }));

      setOrder((order) => ({ ...order, orderRows }));
    };

  const handleFlexibleStartTimeChange: HandleFlexibleStartTimeChangeFunction = (
    shiftRowId,
    time,
  ) => {
    const timeString = time.slice(0, 4);
    if (timeString.length > 0 && timeString.length !== 4) return;

    const shift = order.shiftDetails.find((sd) => sd.id === shiftRowId);
    if (!shift) return;

    const flexUntilTime = timeString
      ? new Date(shift.shiftDetail.time.start).setHours(
          +timeString.slice(0, 2),
          +timeString.slice(2, 4),
        )
      : undefined;

    const shiftDetails = order.shiftDetails.map((sd) =>
      sd.id === shiftRowId
        ? {
            ...sd,
            shiftDetail: {
              ...sd.shiftDetail,
              flexibleStartUntil: flexUntilTime,
            },
          }
        : sd,
    );
    const orderRows = order.orderRows.map((row) => ({
      ...row,
      shiftDetail: { ...row.shiftDetail, flexibleStartUntil: flexUntilTime },
    }));

    setOrder((order) => ({
      ...order,
      shiftDetails,
      orderRows,
    }));
  };

  const handleTeamNotificationSettingsChange = (
    name: "notifyWhenFulfilled" | "notifyMinutesBeforeIfNotFulfilled",
    value: boolean | number,
  ) => {
    const teamNotificationSettings = {
      ...order.teamNotificationSettings,
      [name]: value,
    };

    setOrder((order) => ({
      ...order,
      teamNotificationSettings,
    }));
  };

  const onSave = (reasonForEdit: string) => {
    dispatch(
      saveOrderRow({
        ...order,
        reasonForEdit,
        editor: currentUserFullName,
        automatchingStatus: order.status.automatchingStatus,
        automatchingStartDelayMinutes:
          order.status.automatchingStartDelayMinutes ?? null,
        version: initialOrder.version, // initialOrder version is updated in redux, but not in the local state
      }),
    )
      .then((action) => {
        if (isFulfilled(action)) {
          if (removeShiftsFromBundleIds.length > 0) {
            const data = {
              bundleId: order.bundleId,
              orderRowIds: removeShiftsFromBundleIds,
            };
            dispatch(disassembleOrderRowBundle(data)).then((action) => {
              if (isFulfilled(action)) {
                setRemoveShiftsFromBundleIds([]);
                // TODO get new original bundle id from response and navigate (stay in edit view)
                navigate("/home/orders");
                sendMessage(t("orderSaved"), "success");
              }
            });
          } else {
            getOrderFulfilledStatus();
            sendMessage(t("orderSaved"), "success");
          }
        }
      })
      .finally(() => setOpenMode(OrderDialogMode.None));
  };

  const handleTitleDateDisplay = () => {
    const start = new Date(order.orderRows[0].shiftDetail.time.start);
    const end = new Date(
      order.orderRows[order.orderRows.length - 1].shiftDetail.time.end,
    );

    if (start.getDate() < end.getDate()) {
      return `${format(start, leadingZerosDateFormat)} - ${format(end, leadingZerosDateFormat)}`;
    } else {
      return `${format(start, leadingZerosDateFormat)}`;
    }
  };

  const titleTime =
    order.orderRows.length > 1
      ? ""
      : `${format(
          new Date(order.orderRows[0].shiftDetail.time.start),
          "HH:mm",
        )} - ${format(
          new Date(order.orderRows[0].shiftDetail.time.end),
          "HH:mm",
        )}`;

  const removeShiftsFromBundle = (
    shifts: { id: string; version: number }[],
  ) => {
    setRemoveShiftsFromBundleIds((s) => [...s, ...shifts]);
    const shiftIds = shifts.map((shift) => shift.id);
    // TODO: get rid of orderRows references after that field is deprecated
    const newOrderRows = order.orderRows.filter(
      (row) => !shiftIds.includes(row.id),
    );
    const newShiftDetails = order.shiftDetails.filter(
      (sd) => !shiftIds.includes(sd.orderRowId),
    );
    setOrder({
      ...order,
      orderRows: newOrderRows,
      shiftDetails: newShiftDetails,
    });
    setOpenMode(OrderDialogMode.None);
  };

  const openSearchDialog = () => {
    setOpenMode(OrderDialogMode.Search);
  };

  useEffect(() => {
    if (bundleId && bundleId !== "") {
      dispatch(fetchOrderRowsOfferCount(bundleId)).then((action) => {
        if (isFulfilled(action)) {
          setOffers(action.payload);
        }
      });
    }
  }, [bundleId, dispatch]);

  useEffect(() => {
    if (
      !(order.status?.automatchingStatus === AUTOMATCHING_ON) &&
      order.orderQueueId
    ) {
      dispatch(fetchOrderQueueById(order.orderQueueId)).then((action) => {
        if (isFulfilled(action)) {
          const handlers = action.payload.members.map((handler) => ({
            id: handler.id,
            name: `${handler.firstName} ${handler.lastName}`,
          }));
          setOrderHandlerOptions(handlers);
        }
      });
    }
  }, [order.status?.automatchingStatus, order.orderQueueId, dispatch]);

  const periodStartDateIsValid = periodStartDateIsRequired
    ? order.orderRows.every(
        (row) =>
          row.taskSpec.periodStartDate !== null &&
          row.taskSpec.periodStartDate !== undefined &&
          isValid(new Date(row.taskSpec.periodStartDate as string)),
      )
    : order.orderRows.every(
        (row) =>
          isValid(new Date(row.taskSpec.periodStartDate as string)) ||
          row.taskSpec.periodStartDate === null ||
          row.taskSpec.periodStartDate === undefined,
      );

  const hasDescriptionToEezyAndCustomerAndTalentChanges =
    isExpertAuthorizedToEdit &&
    order.orderRows.some(
      (row, index) =>
        row.descriptionToEezyAndCustomerAndTalent !==
        initialOrder.orderRows[index]?.descriptionToEezyAndCustomerAndTalent,
    );

  const firstShiftStartMs = Math.min(
    ...order.orderRows.map((row) =>
      new Date(row.shiftDetail.time.start).getTime(),
    ),
  );
  const isUserEditingAutomatching =
    order.status?.automatchingStatus !==
      initialOrder.status?.automatchingStatus ||
    order.status?.automatchingStartDelayMinutes !==
      initialOrder.status?.automatchingStartDelayMinutes;
  const isEditedOrderAutomatchingOn =
    order.status?.automatchingStatus === AUTOMATCHING_ON ||
    order.status?.automatchingStatus === AUTOMATCHING_PAUSED;
  const automatchingDelayMinutes = order.status.automatchingStartDelayMinutes;
  const hasTooLongAutomatchingDelay =
    isUserEditingAutomatching &&
    isEditedOrderAutomatchingOn &&
    !order.status.isAutomatchingExhausted &&
    isAutomatchingDelayTooLong({
      delayMinutes: automatchingDelayMinutes,
      firstShiftStart: firstShiftStartMs,
    });
  const start = new Date(order.orderRows[0].shiftDetail.time.start);
  const triesToAutomatchInThePast =
    isEditedOrderAutomatchingOn && !automatchingIsNotInThePast(start);
  const automatchingDelayWarning = hasTooLongAutomatchingDelay
    ? t("order:automatchingDelayTooLong")
    : undefined;

  const disableSave =
    (isReadOnly && !hasDescriptionToEezyAndCustomerAndTalentChanges) ||
    !periodStartDateIsValid ||
    !isEmpty(validationErrors) ||
    hasTooLongAutomatchingDelay ||
    triesToAutomatchInThePast;

  return (
    <>
      {isLoading ? (
        <LoadingSpinner fullSize />
      ) : (
        <Box>
          <FulfillmentStatusNotification
            status={order.status?.fulfillmentStatus}
          />
          <CancelReasonBanner reasonText={order.cancelReason} />
          <Box
            width="90%"
            maxWidth="1400px"
            minWidth="910px"
            paddingRight="16px"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate("/home/orders")}>
                <ArrowLeft style={{ width: "24px", height: "24px" }} />
              </IconButton>
              <OrdersHeader
                title={order.title || order.orderRowTemplateName}
                customerCompany={order.customerOrgName}
                orderRowCount={order.orderRows.length}
                dateInformation={handleTitleDateDisplay()}
                timeInformation={titleTime}
              />
            </Box>
            <OrderHandlerSelection
              bundleId={order.bundleId}
              status={initialOrder.status}
              handlers={order.orderHandlers}
              handlerOptions={orderHandlerOptions}
            />

            {/*
                HIDDEN FOR NOW, IN CASE SOMEONE WANTS IT BACK
                https://eezycore.atlassian.net/browse/YLPE-2392

                !isProdEnv() && !isUatEnv() && (
                  <RoundedButton
                  variant="outlined"
                  sx={{ height: "36px", margin: "auto 0" }}
                  onClick={() => navigate(`/home/jobAds/new?order=${bundleId}`)}
                  >
                  {t("order:createJobAd")}
                  </RoundedButton>
                  )
              */}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Tabs
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "capitalize",
                    fontWeight: 700,
                  },
                }}
                value={tabIndex}
                onChange={handleTabChange}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.label}
                    value={tab.value}
                    label={
                      <Typography color="textSecondary" variant="header">
                        {tab.label}
                      </Typography>
                    }
                  />
                ))}
              </Tabs>
            </Grid>
            <Grid xs={12} ml={2}>
              {tabIndex === 0 && (
                <OrderShortDataProvider>
                  <EditShortOrderInfo
                    orderRow={order}
                    offers={offers}
                    options={options}
                    openSearchDialog={openSearchDialog}
                    handleAmountChange={handleAmountChange}
                    handleTimeChange={handleTimeChange}
                    handleCustomerCCChange={handleCustomerCCChange}
                    handleDescriptionsChange={handleDescriptionsChange}
                    handleAdditionalInfoChange={handleAdditionalInfoChange}
                    handleSalaryLimitChange={handleSalaryLimitChange}
                    handlePeriodStartDateChange={handlePeriodStartDateChange}
                    handleRequirementsChange={handleRequirementsChange}
                    handleFillShiftChange={handleFillShiftChange}
                    handleSkillsChange={handleSkillsChange}
                    handleCancelAssignment={handleCancelAssignmentInfo}
                    handleCancelAllAssignments={handleCancelAllAssignments}
                    handleAutomatchingStatusChange={
                      handleAutomatchingStatusChange
                    }
                    handleAutomatchingDelayChange={
                      handleAutomatchingDelayChange
                    }
                    handleSupplementsChange={handleSupplementsChange}
                    handleSupplementPriceOverridesChange={
                      handleSupplementPriceOverridesChange
                    }
                    isAutomatchingOn={isAutomatchingOn}
                    isExpertAuthorizedToEdit={isExpertAuthorizedToEdit}
                    handleTesVariationChange={handleTesVariationChange}
                    periodStartDateIsRequired={periodStartDateIsRequired}
                    setPeriodStartDateIsRequired={setPeriodStartDateIsRequired}
                    handleFlexibleStartTimeChange={
                      handleFlexibleStartTimeChange
                    }
                    handleTeamNotificationSettingsChange={
                      handleTeamNotificationSettingsChange
                    }
                    setValidationErrors={setValidationErrors}
                    isOrderFulfilled={isOrderFulfilled}
                    automatchingDelayWarning={automatchingDelayWarning}
                    automatchingInitialStatus={
                      initialOrder.status.automatchingStatus
                    }
                    isReadOnly={isReadOnly}
                  />
                </OrderShortDataProvider>
              )}
              {tabIndex === 1 && (
                <ShortOrderWorkOffers
                  bundleId={order.bundleId}
                  disableManualOffers={
                    isAutomatchingOn || !isExpertAuthorizedToEdit || isReadOnly
                  }
                  openSearchDialog={openSearchDialog}
                  isOrderFulfilled={isOrderFulfilled}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <EditOrderActionBanner
                openCancelDialog={() => setOpenMode(OrderDialogMode.CancelRows)}
                onSave={() => setOpenMode(OrderDialogMode.Edit)}
                disableCancel={isCancelDisabled}
                disableSave={disableSave}
              />
            </Grid>
          </Grid>
          <EditOrderDialogs
            openMode={openMode}
            closeDialog={() => {
              setCancelAssignmentIdAndRowId({ assignmentId: "", rowId: "" });
              setCancelAssignmentScope(CancelAssignmentScope.Single);
              setCancelledShifts([]);
              setCancelAssignmentTalentId(null);
              setOpenMode(OrderDialogMode.None);
            }}
            onSaveOrderRows={onSave}
            isLoading={isLoading}
            cancelOrderRows={cancelOrderRows}
            orderRow={order}
            cancelAssignment={cancelAssignment}
            cancelAssignmentPaySalary={cancelAssignmentPaySalary}
            cancelAssignmentScope={cancelAssignmentScope}
            cancelledShifts={cancelledShifts}
            costCenters={options.costCenters}
            removeShiftsFromBundle={removeShiftsFromBundle}
            isAutomatchingOn={isAutomatchingActiveOrPaused}
          />
        </Box>
      )}
    </>
  );
};
